<template>
  <AppResizableDropdown
    ref="dropdownRef"
    class="rules-selector-dropdown"
    position="bottom"
    :content-z-index="10002"
    :fullscreen-on-mobile="false"
  >
    <template #activator>
      <div
        class="app-select__trigger"
        :class="{ 'app-select__trigger--disabled': disabled }"
        :style="customTriggerStyle"
        @click="disabled && $event.stopPropagation()"
      >
        <span class="app-select__single-value">{{ selectedLabel }}</span>
        <AppIcon
          class="app-select__arrow"
          name="chevron-down"
          width="16px"
          color="#979797"
        />
      </div>
    </template>

    <div class="rules-selector-dropdown__options">
      <template v-for="(item, index) in items" :key="String(item.id)">
        <div v-if="index > 0" class="rules-selector-dropdown__divider" />
        <button
          type="button"
          class="rules-selector-dropdown__option"
          :class="{
            'rules-selector-dropdown__option--selected': modelValue === item.id,
            'rules-selector-dropdown__option--disabled': item.disabled,
          }"
          :disabled="item.disabled"
          @click.stop="select(item.id)"
        >
          {{ item.label }}
        </button>
      </template>
    </div>
  </AppResizableDropdown>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, type CSSProperties } from 'vue';
import AppResizableDropdown from '@/components/ui-kit/dropdown/AppResizableDropdown.vue';
import AppIcon from '@/components/ui-kit/icon/AppIcon.vue';
import type { SelectOption } from '@/components/ui-kit/select/AppSelect.vue';

const props = withDefaults(defineProps<{
  modelValue: string | number;
  items: SelectOption[];
  disabled?: boolean;
  customTriggerStyle?: CSSProperties;
}>(), {
  disabled: false,
  customTriggerStyle: () => ({}),
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void;
}>();

const dropdownRef = ref<InstanceType<typeof AppResizableDropdown> | null>(null);

const selectedLabel = computed(() =>
  props.items.find((item) => item.id === props.modelValue)?.label ?? '',
);

function select(id: string | number) {
  if (props.disabled) return;
  emit('update:modelValue', id);
  nextTick(() => dropdownRef.value?.close());
}
</script>

<style scoped lang="scss">
@import '@/styles/color.scss';

.rules-selector-dropdown {
  &__options {
    display: flex;
    width: 130px;
    flex-direction: column;
    padding: 4px;
  }

  &__divider {
    height: 1px;
    background-color: $color-divider;
    width: 122px;
  }

  &__option {
    width: 122px;
    height: 28px;
    padding: 3px 12px 3px 8px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: $color-text_main;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:hover {
      background-color: $color-divider;
    }

    &--selected {
      color: $color-accent;

      &:hover {
        background-color: $background-card;
      }
    }

    &--disabled {
      color: $color-text_unavailable;
      cursor: not-allowed;
    }
  }
}

.app-select {
  &__trigger {
    display: flex;
    min-width: 134px;
    min-height: 26px;
    align-items: center;
    gap: 8px;
    padding: 2px 8px;
    justify-content: normal !important;
    border: 1px solid $color-border;
    border-radius: 4px;
    background-color: #fff;
    box-sizing: border-box;
    cursor: pointer;

    &--disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  &__single-value {
    overflow: hidden;
    color: $color-text_main;
    font-size: 14px;
    line-height: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__arrow {
    flex-shrink: 0;
  }
}
</style>
